import { ref } from 'vue'
import { defineStore } from 'pinia'
import uuidMix from './mix/uuid'

export const useTerritorioStore = defineStore('territorio', () => {
  const uuid = ref()
  const zona = ref('')
  const numero = ref()
  const referencia = ref('')
  const notas = ref('')
  const center = ref(null)
  const shape = ref(0)
  const angle = ref(-19)
  const zoom = ref(17)
  const limits = ref([])
  const mapConfig = ref({})
  const tipo = ref('default')
  const routes = ref([])
  const color = ref({
    backgroundColor: 'rgb(255, 255, 254)',
    color: '#000000',
  })
  const mzNumbers = ref([])
  const puntoEncuentro = ref([])
  const grupo = ref(null)
  const tamanoGrupo = ref(null)
  const aptoDuranteLluvia = ref(null)
  const aptoLuegoDeLluvia = ref(null)
  const tipoVehiculo = ref([])
  const seguridad = ref(null)
  const nivelProductividad = ref(null)
  const publicadoresCerca = ref('')
  const observaciones = ref('')
  const estudios = ref([])
  const transporte = ref([])

  const clear = () => {
    uuid.value = undefined
    zona.value = ''
    numero.value = ''
    referencia.value = ''
    notas.value = ''
    center.value = null
    shape.value = 0
    angle.value = -19
    zoom.value = 17
    limits.value = []
    mapConfig.value = {}
    tipo.value = 'default';
    routes.value = []
    color.value = {
      backgroundColor: 'rgb(255, 255, 254)',
      color: '#000000',
    }
    mzNumbers.value = []
    puntoEncuentro.value = []
    tamanoGrupo.value = null
    aptoDuranteLluvia.value = null
    aptoLuegoDeLluvia.value = null
    tipoVehiculo.value = []
    grupo.value = null
    seguridad.value = null
    nivelProductividad.value = null
    publicadoresCerca.value = ''
    observaciones.value = ''
    estudios.value = []
    transporte.value = []
  }

  // Carga un territorio de la lista para editarlo. Limpia antes: $patch no resetea
  // lo que el nuevo territorio no trae (p. ej. grupo) y arrastraría el anterior.
  // caracteristicas llega anidado del servidor; el store las expone planas.
  const cargar = (doc) => {
    clear()
    const { caracteristicas, ...rest } = doc
    const refs = {
      uuid, zona, numero, referencia, notas, center, shape, angle, zoom, limits, mapConfig,
      tipo, routes, color, mzNumbers, puntoEncuentro, estudios, transporte,
      grupo, tamanoGrupo, aptoDuranteLluvia, aptoLuegoDeLluvia, tipoVehiculo, seguridad,
      nivelProductividad, publicadoresCerca, observaciones,
    }
    const data = { ...rest, ...(caracteristicas || {}) }
    for (const [key, r] of Object.entries(refs)) {
      if (data[key] !== undefined) r.value = data[key]
    }
    if (!doc.tipo) tipo.value = 'default'
  }

  const setTerritorio = (data) => {
    // clear
    clear()
    // set
    uuid.value = data.uuid
    zona.value = data.zona || ''
    numero.value = data.numero
    referencia.value = data.referencia
    notas.value = data.notas
    center.value = data.center
    shape.value = data.shape
    angle.value = data.angle
    zoom.value = data.zoom
    limits.value = data.limits
    mapConfig.value = data.mapConfig
    color.value = data.color
    mzNumbers.value = data.mzNumbers
    puntoEncuentro.value = data.puntoEncuentro
    const car = data.caracteristicas || {}
    grupo.value = car.grupo ?? null
    tamanoGrupo.value = car.tamanoGrupo ?? null
    aptoDuranteLluvia.value = car.aptoDuranteLluvia ?? null
    aptoLuegoDeLluvia.value = car.aptoLuegoDeLluvia ?? null
    tipoVehiculo.value = car.tipoVehiculo ?? []
    seguridad.value = car.seguridad ?? null
    nivelProductividad.value = car.nivelProductividad ?? null
    publicadoresCerca.value = car.publicadoresCerca ?? ''
    observaciones.value = car.observaciones ?? ''
    estudios.value = data.estudios || []
    transporte.value = data.transporte || []
  }

  const addLimit = (latlng) => {
    limits.value.push(latlng)
  }
  function removeLastLimit(){
    let list = [...limits.value]
    list.pop()
    limits.value = list
  }
  const addMzNumber = (latlng) => {
    mzNumbers.value.push(latlng)
  }
  function removeLastMz(){
    let list = [...mzNumbers.value]
    list.pop()
    mzNumbers.value = list
  }
  const addPuntoEncuentro = (data) => {
    puntoEncuentro.value.push(data)
  }
  function removeLastPE(){
    let list = [...puntoEncuentro.value]
    list.pop()
    puntoEncuentro.value = list
  }




  const setId = async () => {
    uuid.value = await uuidMix.new()
  }

  const getData = () => {
    return {
      uuid: uuid.value,
      numero: numero.value,
      zona: zona.value,
      center: JSON.parse(JSON.stringify(center.value)),
      color: JSON.parse(JSON.stringify(color.value)),
      mapConfig: JSON.parse(JSON.stringify(mapConfig.value)),
      limits: JSON.parse(JSON.stringify(limits.value)),
      mzNumbers: JSON.parse(JSON.stringify(mzNumbers.value)),
      // La dirección es lo importante; si falta el nombre se usa la dirección
      puntoEncuentro: JSON.parse(JSON.stringify(puntoEncuentro.value)).map((p) => ({
        ...p,
        name: p.name?.trim() || p.direccion || p.name || '',
      })),
      tipo: tipo.value,
      routes: JSON.parse(JSON.stringify(routes.value)),
      shape: shape.value, 
      angle: angle.value,
      zoom: zoom.value,
      referencia: referencia.value,
      notas: notas.value,
      caracteristicas: {
        grupo: grupo.value,
        tamanoGrupo: tamanoGrupo.value,
        aptoDuranteLluvia: aptoDuranteLluvia.value,
        aptoLuegoDeLluvia: aptoLuegoDeLluvia.value,
        tipoVehiculo: tipoVehiculo.value,
        seguridad: seguridad.value,
        nivelProductividad: nivelProductividad.value,
        publicadoresCerca: publicadoresCerca.value,
        observaciones: observaciones.value,
      },
      estudios: JSON.parse(JSON.stringify(estudios.value)),
      transporte: JSON.parse(JSON.stringify(transporte.value)),
    }
  }
  return { 
    uuid,
    mapConfig,
    numero, zona, center, limits, mzNumbers, shape, 
    angle,
    zoom,
    referencia,
    notas,
    color,
    tipo,
    routes,
    puntoEncuentro,
    addLimit,
    removeLastLimit,
    addMzNumber,
    removeLastMz,
    addPuntoEncuentro,
    removeLastPE,
    setId,
    getData,
    clear,
    cargar,
    grupo,
    tamanoGrupo,
    aptoDuranteLluvia,
    aptoLuegoDeLluvia,
    tipoVehiculo,
    seguridad,
    nivelProductividad,
    publicadoresCerca,
    observaciones,
    estudios,
    transporte,
  }
}
)
